<template>
    <div class="flex flex-col gap-2 w-full min-h-full">
        <div class="flex gap-2 w-full adaptiv-container">
            <h2>Dashboard</h2>
            <div class="flex gap-2 w-full adaptiv-container-selectors">
                <div class="w-full max-w-[360px]">
                    <FloatLabel class="w-full max-w-[360px]" variant="in">
                        <MultiSelect 
                            v-model="currentObject" 
                            :options="objects" 
                            optionLabel="name"  
                            :maxSelectedLabels="3" 
                            class="w-full max-w-[360px]"
                            filter
                            @change="updateChartData"
                        />
                        <label for="in_label">Объект</label>
                    </FloatLabel>
                </div>
                <div class="w-full max-w-[360px]">
                    <FloatLabel class="w-full max-w-[360px]" variant="in">
                        <MultiSelect 
                            v-model="currentPeriod" 
                            :options="weeks" 
                            optionLabel="name"  
                            :maxSelectedLabels="3" 
                            class="w-full max-w-[360px]"
                            @change="loadChartData"
                            filter
                        />
                        <label for="in_label">Неделя</label>
                    </FloatLabel>
                </div>
            </div>
        </div>

        <div v-if="currentObject && currentObject.length === 0" class="flex w-full">
            <h6>Объект не выбран</h6>
        </div>

        <div v-if="loading" class="flex justify-center w-full">
            <Spinner />
        </div>

        <div v-else class="overflow-y-scroll h-[calc(100vh-60px-32px)]">
            <div v-if="currentObject && currentObject.length !== 0" class="w-full h-auto flex flex-column gap-4">
                <div class="flex gap-4 w-full adaptiv-owerlay h-auto">
                    <Card v-if="shouldDisplayObjectFeeLineChart" class="adaptiv-width h-[500px]">
                        <template #content>
                            <div class="w-full h-full">
                                <h6>Клубный fee</h6>
                                <div class="flex flex-col align-center justify-center w-full h-full">
                                    <Chart type="line" :data="objectFeeLineChart" :options="lineChartObjectFeeOptions" class="w-full h-[420px]" />
                                </div>
                            </div>
                        </template>
                    </Card>
                    <Card v-if="shouldDisplayActivePlayers" class="adaptiv-width h-[500px]">
                        <template #content>
                            <div class="w-full h-full">
                                <h6>Активные игроки</h6>
                                <div class="flex flex-col align-center justify-center w-full h-full">
                                    <Chart type="line" :data="activePlayersLineChart" :options="activePlayersLineChartOptions" class="w-full h-[420px]" />
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <div v-if="shouldDisplayInactivePlayers" class="flex gap-4 w-full min-h-[500px] h-auto">
                    <Card class="w-full h-full">
                        <template #content>
                            <div class="w-full h-full flex flex-col gap-2">
                                <h6>Неактивные игроки</h6>
                                <div v-for="(data, index) in inactivePlayers" :key="index" class="flex align-start gap-4 w-full h-auto min-h-[500px] inactive-card">
                                    <Chart type="bar" :data="data.chart" :options="inactivePlayersBarChartOptions" class="adaptiv-width h-[500px]" />
                                    <DataTable :value="data.table" showGridlines class="adaptiv-width no-footer" >
                                        <Column field="id" header="Id" class="w-1/3"></Column>
                                        <Column field="name" header="Игрок" class="w-1/3"></Column>
                                        <Column field="lastGame" header="Последняя игра" class="w-1/3">></Column>
                                    </DataTable>
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <div class="flex gap-4 w-full h-auto"> 
                    <Card v-if="shouldDisplayRaykomesBarChart" class="w-full min-h-[500px] h-auto">
                        <template #content>
                            <div class="w-full h-full">
                                <h6>Рейкомесы</h6>
                                <div class="flex flex-col align-center justify-center w-full h-full">
                                    <Chart type="bar" :data="raykomesBarChart" :options="lineChartRaykomesFeeOptions" class="w-full h-[500px]" />
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <div v-if="shouldDisplayHandsByGameTypeCharts || shouldDisplayFeeByGameTypeCharts" class="w-full min-h-[300px] h-auto">
                    <Card>
                        <template #content>
                            <div class="w-full flex flex-col gap-4 h-auto">
                                <h6>Рейк по типам игр</h6>
                                <div class="flex flex-wrap gap-4 justify-around min-h-[300px] h-auto">
                                    <div v-for="(item, index) in feeByGameTypeCharts" :key="index" class="relative w-[300px] h-[300px]">
                                        <div class="absolute z-1 inset-0 flex flex-col items-center justify-center">
                                            <Chart 
                                                type="doughnut" 
                                                :data="item.chartData" 
                                                :options="chartOptions" 
                                                class="w-full h-full"
                                            />
                                        </div>
                                        <div class="absolute z-0 inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <p class="text-sm" :style="{ color: item.objectColor }">
                                                {{ item.objectName }}
                                            </p>
                                            <p class="text-2xl font-bold" :style="{ color: item.objectColor }">
                                                {{ item.total.toFixed(2) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex flex-wrap justify-center gap-4">
                                    <div v-for="(item, index) in combinedLegendData" :key="index" class="flex items-center gap-2">
                                        <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: item.color }"></div>
                                        <span class="text-sm">{{ item.label }}</span>
                                    </div>
                                </div>
                                <h6>Количество рук по типам игр</h6>
                                <div class="flex flex-wrap gap-4 justify-around min-h-[300px] h-auto">
                                    <div v-for="(item, index) in handsByGameTypeCharts" :key="index" class="relative w-[300px] h-[300px]">
                                        <div class="absolute z-1 inset-0 flex flex-col items-center justify-center">
                                            <Chart 
                                                type="doughnut" 
                                                :data="item.chartData" 
                                                :options="chartOptions" 
                                                class="w-full h-full"
                                            />
                                        </div>
                                        <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <p class="text-sm" :style="{ color: item.objectColor }">
                                                {{ item.objectName }}
                                            </p>
                                            <p class="text-2xl font-bold" :style="{ color: item.objectColor }">
                                                {{ item.total }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex flex-wrap justify-center gap-4">
                                    <div v-for="(item, index) in combinedLegendData" :key="index" class="flex items-center gap-2">
                                        <div class="w-4 h-4 rounded-full" :style="{ backgroundColor: item.color }"></div>
                                        <span class="text-sm">{{ item.label }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import Chart from 'primevue/chart';
import Card from 'primevue/card';  
import FloatLabel from 'primevue/floatlabel';
import MultiSelect from 'primevue/multiselect';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Row from 'primevue/row';   
import ColumnGroup from 'primevue/columngroup';
import Spinner from '@/components/UI/Spinner.vue'
import { ReportingPeriodsService } from '@/services/reporting.periods';
import { ObjectsService } from '@/services/objects';
import { ReportsService } from '@/services/reports';
import { mapGetters } from 'vuex';
import { isPermitionAllowed } from '@/permissions';

const backgroundColorsList = [
    '--p-orange-500',
    '--yellow', 
    '--red',
    '--green',
    '--blue',
    '--p-orange-900',
    '--p-orange-300',
    '--p-orange-200',
    '--p-orange-700',
    '--p-orange-100',
    '--p-orange-600',
    '--p-orange-400',
];

const doughnutColorsList = [
    '#ffd700',
    '#f0e68c',
    '#808000', 
    '#6b8e23',
    '#ffa07a',
    '#ff8c00',
    '#a0522d',
    '#ff6347',
    '#006400',
];

export default {
    name: "Dashboard",
    components: {
        Chart,
        Card,
        MultiSelect,
        FloatLabel,
        DataTable,
        Column,
        Spinner,
    },
    data() {
        return {
            isInitialized: false,
            currentPeriod: null,
            weeks: [], 
            currentObject: null,
            currentPerson: null, // доступен для выбора админу

            objects: [], 
            loading: true,
            lineChartOptions: null,

            shouldDisplayObjectFeeLineChart: true,
            objectFeeLineChart: null,
            objectFeeLineChartAllData: null,

            shouldDisplayActivePlayers: true,
            activePlayersLineChart: null,
            activePlayersLineChartAllData: null, 

            shouldDisplayInactivePlayers: true,
            inactivePlayers: null,
            inactivePlayersAllData: null,

            shouldDisplayRaykomesBarChart: true,
            raykomesBarChart: null,
            raykomesBarChartAllData: null,

            shouldDisplayFeeByGameTypeCharts: true,
            feeByGameTypeCharts: null,
            feeByGameTypeChartsAllData: null,

            shouldDisplayHandsByGameTypeCharts: true,
            handsByGameTypeCharts: null,
            handsByGameTypeChartsAllData: null,

            combinedLegendData: [],
            abortController: null, 
        };
    },

    computed: {
        ...mapGetters('ui', ['isMobile']),
        ...mapGetters('user', ['getCurrentPerson']),
    },

    mounted() {         
        this.lineChartOptions = this.setLineChartOptions();
        this.lineChartObjectFeeOptions = this.setLineChartObjectFeeOptions();
        this.lineChartRaykomesFeeOptions = this.setLineChartRaykomesOptions();
        this.activePlayersLineChartOptions = this.setLineChartActivePlayersOptions(); 
        this.inactivePlayersBarChartOptions = this.setInactivePlayersBarChartOptions(); 
        this.chartOptions = this.setChartOptions();
    },

    created() {
        this.currentPerson = this.getCurrentPerson;
        console.log('Текущее выбранное физлицо:', this.currentPerson);

        this.setOptions();  
    },   

    // watch: {
    //     // обработа изменения currentPerson в store
    //     getCurrentPerson(newPerson) {
    //         console.log('Выбранное физлицо:', newPerson);
            
    //         if (!this.isInitialized) return;

    //         if (newPerson && this.currentPerson != newPerson) {                
    //             this.currentPerson = newPerson;
    //             this.updateObjectList();                
    //             //this.updateChartData();
    //             this.loadChartData();
    //         }
    //     }, 
    // },

    watch: {
        getCurrentPerson: {
            immediate: false,
            handler: async function (newPerson) {
                console.log('Выбранное физлицо:', newPerson);
                console.log('Текущее физлицо:', this.currentPerson)

                if (!this.isInitialized) return;

                if (newPerson && this.currentPerson !== newPerson) {
                    console.log('Обновляем физлицо');
                    this.currentPerson = newPerson;                    
                    await this.updateObjectList();
                    this.loadChartData();
                }
            }
        }
    },

    methods: { 
        isPermitionAllowed,

        updateChartData() {            
            this.prepareObjectFeeChartData();
            this.prepareRaykomesChartData(); 
            this.prepareFeeByGameTypeCharts();
            this.prepareHandsByGameTypeCharts();
            this.prepareInactivePlayers();
            this.prepareActivePlayersChartData();
        }, 

        async loadChartData() { 
            const idList = this.currentPeriod.map(item => item.id)
            await this.loadDataForPeriod(idList);
        },

        async loadDataForPeriod(idList) {
            if (this.abortController) {
                this.abortController.abort(); 
            }
 
            const currentAbortController = new AbortController();
            this.abortController = currentAbortController;

            this.loading = true; 
            let feeIdList = idList

            if (idList.length < 5) {
                const lastSelectedId = idList[0];
                const selectedIndex = this.weeks.findIndex(item => item.id === lastSelectedId);
                
                const previousFive = this.weeks.slice(
                    Math.max(0, selectedIndex + 1),
                    Math.min(this.weeks.length, selectedIndex + 1 + 5) // Берем максимум 5 недель
                ); 

                feeIdList = [...idList, ...previousFive.map(item => item.id)] 
            }

            const param = {};
            if (this.currentPerson) {
                param['person_guid'] = this.currentPerson.guid; 
            }

            try { 
                const [
                    objectFeeData,
                    raykomesData, 
                    activePlayersData,
                    feeByGameTypeData,
                    handsByGameTypeData,
                    inactivePlayersData
                ] = await Promise.all([
                    ReportsService.getObjectFee(feeIdList, param, {
                        signal: currentAbortController.signal,
                    }),
                    ReportsService.getRaykomes(idList, param, {
                        signal: currentAbortController.signal,
                    }), 
                    ReportsService.getActivePlayers(idList, param, {
                        signal: currentAbortController.signal,
                    }),
                    ReportsService.getFeeByGameType(idList, param, {
                        signal: currentAbortController.signal,
                    }),
                    ReportsService.getHandsByGameType(idList, param, {
                        signal: currentAbortController.signal,
                    }),
                    ReportsService.getInactivePlayers(idList, param, {
                        signal: currentAbortController.signal,
                    })
                ]);

                if (!currentAbortController.signal.aborted) {
                    this.objectFeeLineChartAllData = objectFeeData.data;
                    this.raykomesBarChartAllData = raykomesData.data; 
                    this.activePlayersLineChartAllData = activePlayersData.data;
                    this.feeByGameTypeChartsAllData = feeByGameTypeData.data;
                    this.handsByGameTypeChartsAllData = handsByGameTypeData.data;
                    this.inactivePlayersAllData = inactivePlayersData.data;

                    // this.prepareObjectFeeChartData();
                    // this.prepareRaykomesChartData(); 
                    // this.prepareActivePlayersChartData();
                    // this.prepareFeeByGameTypeCharts();
                    // this.prepareHandsByGameTypeCharts();
                    // this.prepareInactivePlayers();

                    this.updateChartData();
                }
            } catch (error) {
                if (error.name !== 'CanceledError') {
                    console.error('Ошибка загрузки:', error);
                    throw error;
                }
            } finally {
                if (this.abortController === currentAbortController) {
                    this.loading = false;
                }
            }
        },

        // async setOptions() {
        //     this.loading = true
        //     let param = {limit: 20}
        //     try {
        //         const { data: periodsListData } = await ReportingPeriodsService.get(param) ;
        //         this.weeks = periodsListData;                       
        //         if (this.weeks.length > 0 && !this.currentPeriod) {   
        //             this.currentPeriod = [this.weeks[0]];                
        //         } 

        //         let param = {};
        //         if (isPermitionAllowed('gettingObjectListForPerson', this.$store.getters['user/getRoleCurentUser'])) {
        //             param['person_guid'] = this.currentPerson.guid;
        //         }

        //         const { data: objectsListData } = await ObjectsService.getObjectsList(param);
        //         this.objects = objectsListData.filter(object => object.type !== 'player').map((object, index) => ({...object, color: backgroundColorsList[index]}))
        //         if (this.objects.length > 0 && !this.currentObject) {   
        //             this.currentObject = [this.objects[0]];                
        //         }

        //         const idList = [periodsListData[0].id]

        //         if (this.currentPeriod?.length) {
        //             await this.loadDataForPeriod(idList);
        //         }
        //     } catch (error) {
        //         console.error(error);
        //     } finally {
        //         this.isInitialized = true;
        //         this.loading = false;
        //     }
        // },

        async setOptions() {
            this.loading = true;
            
            try {   
                const [periodsListData, objectsListData] = await Promise.all([
                    this.getReportingPeriods(),
                    this.getObjectsList()
                ]);


                this.weeks = periodsListData;                       
                if (this.weeks.length > 0 && !this.currentPeriod) {   
                    this.currentPeriod = [this.weeks[0]];                
                } 

                const idList = [periodsListData[0].id]
                
                this.objects = objectsListData.filter(object => object.type !== 'player').map((object, index) => ({...object, color: backgroundColorsList[index]}))
                if (this.objects.length > 0 && !this.currentObject) {   
                    this.currentObject = [this.objects[0]];                
                }                

                if (this.currentPeriod?.length) {
                    await this.loadDataForPeriod(idList);
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.isInitialized = true;
                this.loading = false;
            }
        },

        async updateObjectList() {
            this.objects = [];
            this.currentObject = [];

            try {
                const objectsListData = await this.getObjectsList();
                console.log('objectsListData:', objectsListData)

                this.objects = objectsListData.filter(object => object.type !== 'player').map((object, index) => ({...object, color: backgroundColorsList[index]}))
                
                this.currentObject = this.objects;
            } catch (error) {
                console.error('Ошибка при получении списка объектов:', error);
            }
        },

        async getObjectsList() {
            let param = {};
            var result = [];
            if (isPermitionAllowed('gettingObjectListForPerson', this.$store.getters['user/getRoleCurentUser']) && this.currentPerson) {
                param['person_guid'] = this.currentPerson.guid;
            }

            console.log('getObjectsList currentPerson', this.currentPerson)

            try {
                const { data } = await ObjectsService.getObjectsList(param);
                result = data            
            } catch (e) {                
                this.error = e.message
                console.log(e)
            } 

            return result;
        },

        async getReportingPeriods() {            
            let param = {limit: 20};
            var result = [];
            const scopes = this.$store.getters['user/getScopesCurentUser']            

            if (scopes?.unions.length > 0) {
                param['union_id'] = scopes.unions[0]; 
            }

            try {
                const { data } = await ReportingPeriodsService.get(param)         
                result = data;            
            } catch (e) {                
                this.error = e.message
                console.log(e)
            } 

            return result;
        },      

        prepareObjectFeeChartData() {
            if (!this.objectFeeLineChartAllData || !this.objectFeeLineChartAllData.length) {
                this.shouldDisplayObjectFeeLineChart = false
                return;
            }

            const filteredData = this.currentObject.map(selectedObj =>  this.objectFeeLineChartAllData.find(player => player.object.id === selectedObj.id)).filter(Boolean); 

            if (!filteredData.length) {
                this.shouldDisplayObjectFeeLineChart = false
                return;
            }

            this.shouldDisplayObjectFeeLineChart = true
            const documentStyle = getComputedStyle(document.documentElement);
            const colorMap = {};
            this.currentObject.forEach(obj => {
                colorMap[obj.id] = documentStyle.getPropertyValue(obj.color);
            });

            const allPeriods = [];
            filteredData.forEach(player => {
                player.data.forEach(entry => {
                    if (!allPeriods.some(p => p.id === entry.period.id)) {
                        allPeriods.push({
                            id: entry.period.id,
                            name: entry.period.name,
                            shortName: entry.period.name.split(' ')[0], // "W52"
                            start: entry.period.start,
                            end: entry.period.end
                        });
                    }
                });
            });
 
            const sortedPeriods = allPeriods.sort((a, b) => new Date(a.start) - new Date(b.start));
 
            const datasets = filteredData.map((player, index) => {
                return {
                    label: player.object.name,
                    data: sortedPeriods.map(period => {
                        const found = player.data.find(entry => entry.period.id === period.id);
                        return found ? found.fee : null;
                    }), 
                    borderColor: colorMap[player.object.id] || '#cccccc',
                    backgroundColor: colorMap[player.object.id] || '#cccccc',
                    tension: 0.4,
                };
            });

            this.objectFeeLineChart = {
                labels: sortedPeriods.map(period => period.name),
                shortLabels: sortedPeriods.map(period => period.shortName),
                datasets
            };
        },

        setLineChartObjectFeeOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue('--p-text-color');
            const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');
            
            return {
                responsive: true,
                maintainAspectRatio: false, 
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: function(context) { 
                                return context[0].label;
                            },
                            label: function(context) {
                                return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}`;
                            }
                        }
                    },
                    legend: {
                        position: 'bottom',
                        labels: {
                            boxWidth: 12,
                            padding: 20,
                            usePointStyle: true, 
                            color: textColor,
                            font: {
                                size: 14
                            }, 
                        }, 
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            callback: function(value) { 
                                return this.chart.data.shortLabels[value];
                            }
                        },
                        title: {
                            display: true,
                            text: 'Неделя'
                        },
                        grid: {
                            color: surfaceBorder
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Сумма', 
                        },
                        beginAtZero: true,
                        grid: {
                            color: surfaceBorder
                        }
                    }
                },
                elements: {
                    line: {
                        tension: 0.4,
                        borderWidth: 2
                    },
                    point: {
                        radius: 6,
                        hoverRadius: 8,
                        hitRadius: 10,
                    }
                }
            }
        },

        prepareRaykomesChartData() {
            if (!this.raykomesBarChartAllData || !this.raykomesBarChartAllData.length) {
                this.shouldDisplayRaykomesBarChart = false;
                return;
            }
 
            const playersSet = new Set();
            this.raykomesBarChartAllData.forEach(obj => {
                obj.data.forEach(item => {
                    playersSet.add(item.player.name);
                });
            });
            const allPlayers = Array.from(playersSet);
 
            const playerMaxFeeMap = new Map();

            allPlayers.forEach(playerName => {
                let maxFee = 0;
                this.raykomesBarChartAllData.forEach(obj => {
                    const item = obj.data.find(d => d.player.name === playerName);
                    if (item && item.fee > maxFee) {
                        maxFee = item.fee;
                    }
                });
                playerMaxFeeMap.set(playerName, maxFee);
            });
 
            const sortedPlayers = Array.from(playerMaxFeeMap.entries())
                .sort((a, b) => b[1] - a[1])
                .map(entry => entry[0]);
 
            const labels = sortedPlayers;
 
            const documentStyle = getComputedStyle(document.documentElement);
            const colorMap = {};
            this.currentObject.forEach(obj => {
                colorMap[obj.id] = documentStyle.getPropertyValue(obj.color);
            });

            const datasetsMap = new Map();
            this.currentObject.forEach(obj => {
                datasetsMap.set(obj.id, {
                    label: obj.name,
                    data: [],
                    backgroundColor: colorMap[obj.id] || '#cccccc',
                    barThickness: 12,
                });
            });
 
            sortedPlayers.forEach(playerName => {
                this.currentObject.forEach(obj => {
                    const dataObj = this.raykomesBarChartAllData.find(o => o.object.id === obj.id);
                    if (!dataObj) {
                        datasetsMap.get(obj.id).data.push(0);
                        return;
                    }
                    const item = dataObj.data.find(d => d.player.name === playerName);
                    datasetsMap.get(obj.id).data.push(item ? item.fee : 0);
                });
            });

            this.raykomesBarChart = {
                labels,
                datasets: Array.from(datasetsMap.values())
            };

            this.shouldDisplayRaykomesBarChart = true;
        },


        setLineChartRaykomesOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue('--p-text-color');
            const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');
            
            return {
                responsive: true,
                maintainAspectRatio: false, 
                indexAxis: 'y',
                plugins: {
                    tooltip: {
                        callbacks: { 
                            label: function(context) { 
                                return `${context.dataset.label}: ${context.parsed.x.toFixed(2)}`;
                            }
                        },
                    },
                    legend: {
                        position: 'bottom', 
                        labels: {
                            boxWidth: 12,
                            padding: 20,
                            usePointStyle: true, 
                            color: textColor,
                            font: {
                                size: 14
                            }
                        }, 
                    }
                },
                scales: {
                    x: { 
                        title: {
                            display: true,
                            text: 'Сумма',
                        },
                        grid: {
                            color: surfaceBorder
                        }
                    },
                    y: { 
                    stacked: true,
                        title: {
                            display: true,
                            text: 'Игроки', 
                        }, 
                        beginAtZero: true,
                        grid: {
                            color: surfaceBorder
                        }, 
                    }
                }
            }
        },

        prepareInactivePlayers() {
            if (!this.inactivePlayersAllData || !this.inactivePlayersAllData.length) {
                this.shouldDisplayInactivePlayers = false;
                return;
            }

            const filteredData = this.currentObject
                .map(selectedObj => this.inactivePlayersAllData.find(player => player.object.id === selectedObj.id))
                .filter(Boolean); 

            if (!filteredData.length) {
                this.shouldDisplayInactivePlayers = false;
                return;
            }

            this.shouldDisplayInactivePlayers = true;
            const documentStyle = getComputedStyle(document.documentElement); 
            const colorMap = {};
            this.currentObject.forEach(obj => {
                colorMap[obj.id] = documentStyle.getPropertyValue(obj.color);
            }); 

            const allPeriods = [];
            filteredData.forEach(obj => {
                obj.chart.forEach(entry => {
                    if (!allPeriods.some(p => p.id === entry.period.id)) {
                        allPeriods.push({
                            id: entry.period.id,
                            name: entry.period.name,
                            shortName: entry.period.code,
                        });
                    }
                });
            });

            this.inactivePlayers = filteredData.map(obj => {  
                const baseColor = colorMap[obj.object.id] || '#cccccc';
                const chartData = {
                    labels: allPeriods.map(period => period.shortName),
                    fullPeriodNames: allPeriods.map(period => period.name),
                    datasets: [{
                        label: obj.object.name,
                        data: allPeriods.map(period => {
                            const found = obj.chart.find(entry => entry.period.id === period.id);
                            return found ? found.count : 0;
                        }),
                        backgroundColor: baseColor,
                        tension: 0.4
                    }]
                };

                const tableData = obj.inactive.map(item => ({
                    ...item.player,
                    lastGame: new Date(item.lastGame).toLocaleDateString('ru-RU'), 
                }));

                return {
                    objectName: obj.object.name,
                    objectColor: baseColor,
                    chart: chartData,
                    table: tableData
                };
            });
        },

        setInactivePlayersBarChartOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue('--p-text-color');
            const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');

            return {
                responsive: true,
                maintainAspectRatio: false, 
                plugins: {
                    legend: {
                        position: 'bottom', 
                        labels: {
                            boxWidth: 12,
                            padding: 20,
                            usePointStyle: true, 
                            color: textColor,
                            font: {
                                size: 14
                            }
                        }, 
                    },
                    tooltip: {
                        callbacks: { 
                            label: (context) => {
                                const count = context.parsed.y || 0;
                                return `${context.dataset.label}: ${count} ${this.pluralizePlayers(count)}`;
                            },
                            title: (context) => { 
                                return this.activePlayersLineChart.fullPeriodNames[context[0].dataIndex];
                            },
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Количество игроков'
                        },
                        grid: {
                            color: surfaceBorder
                        },
                        ticks: {
                            stepSize: 1,  
                            callback: function(value) { 
                                return Number.isInteger(value) ? value : '';
                            }
                        },
                    },
                    x: {
                        title: {
                            display: true,
                            text: 'Недели'
                        },
                        grid: {
                            color: surfaceBorder
                        }
                    }
                }
            }
        },

        prepareActivePlayersChartData() {
            if (!this.activePlayersLineChartAllData || !this.activePlayersLineChartAllData.length) {
                this.shouldDisplayActivePlayers = false;
                return;
            }

            const filteredData = this.currentObject
                .map(selectedObj => this.activePlayersLineChartAllData.find(player => player.object.id === selectedObj.id))
                .filter(Boolean); 

            if (!filteredData.length) {
                this.shouldDisplayActivePlayers = false;
                return;
            }

            this.shouldDisplayActivePlayers = true;
            const documentStyle = getComputedStyle(document.documentElement);
            const colorMap = {};
            this.currentObject.forEach(obj => {
                colorMap[obj.id] = documentStyle.getPropertyValue(obj.color);
            });
 
            const allPeriods = [];
            filteredData.forEach(player => {
                player.chart.forEach(entry => {
                    if (!allPeriods.some(p => p.id === entry.period.id)) { 
                        allPeriods.push({
                            id: entry.period.id,
                            name: entry.period.name,
                            shortName: entry.period.code, 
                        });
                    }
                });
            });

            this.activePlayersLineChart = {
                labels: allPeriods.map(period => period.shortName), 
                fullPeriodNames: allPeriods.map(period => period.name), 
                datasets: filteredData.map(player => {
                    const baseColor = colorMap[player.object.id] || '#cccccc';
                    return {
                        label: player.object.name,
                        data: allPeriods.map(period => {
                            const found = player.chart.find(entry => entry.period.id === period.id);
                            return found ? found.count : null;
                        }),
                        fill: false,
                        borderColor: baseColor,
                        backgroundColor: baseColor,
                        tension: 0.4,
                        spanGaps: true,
                        borderWidth: 2
                    };
                })
            };
        },

        setLineChartActivePlayersOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue('--p-text-color');
            const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');
            
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: (context) => { 
                                return this.activePlayersLineChart.fullPeriodNames[context[0].dataIndex];
                            },
                            label: (context) => {
                                const count = context.parsed.y || 0;
                                return `${context.dataset.label}: ${count} ${this.pluralizePlayers(count)}`;
                            }
                        }
                    },
                    legend: {
                        position: 'bottom',
                        labels: {
                            boxWidth: 12,
                            padding: 20,
                            usePointStyle: true,
                            color: textColor,
                            font: {
                                size: 14
                            }
                        },
                    }
                }, 
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Неделя',
                        },
                        grid: {
                            color: surfaceBorder, 
                        }
                    },
                    y: { 
                        title: {
                            display: true,
                            text: 'Количество игроков',
                        },
                        beginAtZero: true,
                        min: 0,
                        grid: {
                            color: surfaceBorder, 
                        },
                        ticks: {
                            stepSize: 1,  
                            callback: function(value) { 
                                return Number.isInteger(value) ? value : '';
                            }
                        },
                    }
                },
                elements: {
                    line: {
                        tension: 0.4,
                        borderWidth: 2
                    },
                    point: {
                        radius: 6,
                        hoverRadius: 8,
                        hitRadius: 10,
                    }
                }
            };
        },

        prepareFeeByGameTypeCharts() {
            if (!this.feeByGameTypeChartsAllData || !this.feeByGameTypeChartsAllData.length) {
                this.shouldDisplayFeeByGameTypeCharts = false;
                return;
            }

            const filteredData = this.currentObject
                .map(selectedObj => ({
                    ...this.feeByGameTypeChartsAllData.find(player => player.object.id === selectedObj.id),
                    objectColor: selectedObj.color
                }))
                .filter(Boolean);

            if (!filteredData.length) {
                this.shouldDisplayFeeByGameTypeCharts = false;
                return;
            }

            this.shouldDisplayFeeByGameTypeCharts = true;
            const documentStyle = getComputedStyle(document.documentElement);


            console.log('filteredData', filteredData)

            if (!filteredData) {
                console.log('obj.data', filteredData)
                return;
            }

            this.feeByGameTypeCharts = filteredData.map(obj => {                
                // let gameData, data, labels, total = [];
                
                // if ('data' in obj) {                    
                //     gameData = obj.data
                //         .filter(item => !item.game.includes('Объект не найден')) 

                //     data = gameData.map(item => item.fee);
                //     labels = gameData.map(item => item.game);
                //     total = gameData.reduce((sum, item) => sum + item.fee, 0);
                // }    

                const gameData = obj.data
                        ? obj.data.filter(item => !item.game.includes('Объект не найден'))
                        : [];

                const backgroundColor = gameData 
                        ? gameData.map(item => this.getOrCreateColor(item.game)) 
                        : [];

                return {
                    objectName: obj.object?.name,
                    objectColor: documentStyle.getPropertyValue(obj.objectColor),
                    chartData: {
                        labels: gameData.map(item => item.game),
                        //labels: labels,
                        datasets: [{
                             data: gameData.map(item => item.fee),
                            //data: data,
                            backgroundColor: backgroundColor,
                            borderColor: documentStyle.getPropertyValue('--p-card-background'),
                            borderRadius: 6,
                            borderWidth: 2,
                        }]
                    },
                    //total: total
                    total: gameData.reduce((sum, item) => sum + item.fee, 0)
                };
            });
        },

        prepareHandsByGameTypeCharts() {
            if (!this.handsByGameTypeChartsAllData || !this.handsByGameTypeChartsAllData.length) {
                this.shouldDisplayHandsByGameTypeCharts = false;
                return;
            }

            const filteredData = this.currentObject
                .map(selectedObj => ({
                    ...this.handsByGameTypeChartsAllData.find(player => player.object.id === selectedObj.id),
                    objectColor: selectedObj.color
                }))
                .filter(Boolean);

            if (!filteredData.length) {
                this.shouldDisplayHandsByGameTypeCharts = false;
                return;
            }

            this.shouldDisplayHandsByGameTypeCharts = true;
            const documentStyle = getComputedStyle(document.documentElement);

            this.handsByGameTypeCharts = filteredData.map(obj => {
                const gameData = obj.data
                        ? obj.data.filter(item => !item.game.includes('Объект не найден'))
                        : [];

                const backgroundColor = gameData
                        ? gameData.map(item => this.getOrCreateColor(item.game))
                        : [];                

                return {
                    objectName: obj.object?.name,
                    objectColor: documentStyle.getPropertyValue(obj.objectColor),
                    chartData: {
                        labels: gameData.map(item => item.game),
                        datasets: [{
                            data: gameData.map(item => item.hands),
                            backgroundColor: backgroundColor,
                            borderColor: documentStyle.getPropertyValue('--p-card-background'),
                            borderRadius: 6,
                            borderWidth: 2,
                        }]
                    },
                    total: gameData.reduce((sum, item) => sum + item.hands, 0)
                };
            });
        },

        setChartOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue('--p-text-color');

            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const percent = Math.round((context.raw / total) * 100);
                                return `${context.label}: ${context.raw.toFixed(2)} (${percent}%)`;
                            }
                        },
                        enabled: true,
                        position: 'nearest',
                        external: (context) => { 
                            const {chart, tooltip} = context;
                            if (tooltip && tooltip.opacity > 0) {
                                // Находим DOM-элемент тултипа
                                const tooltipEl = chart.canvas.parentNode.querySelector('.chartjs-tooltip');
                                if (tooltipEl) {
                                tooltipEl.style.zIndex = '9999000';
                                tooltipEl.style.position = 'absolute';
                                }
                            }
                        }
                    }
                },
                cutout: '65%'
            };
        },

        pluralizePlayers(count) {
            count = Math.abs(count) % 100;
            const lastDigit = count % 10;
            
            if (count > 10 && count < 20) return 'игроков';
            if (lastDigit === 1) return 'игрок';
            if (lastDigit >= 2 && lastDigit <= 4) return 'игрока';
            return 'игроков';
        },

        getOrCreateColor(game) {
            const existingItem = this.combinedLegendData.find(item => item.label === game);
            if (existingItem) {
                return existingItem.color;
            }

            const newColor = doughnutColorsList[this.combinedLegendData.length % doughnutColorsList.length];

            this.combinedLegendData.push({
                label: game,
                color: newColor
            });
            console.log('this.combinedLegendData.', this.combinedLegendData)
            return newColor;
        },

        setLineChartOptions() {
            const documentStyle = getComputedStyle(document.documentElement);
            const textColor = documentStyle.getPropertyValue('--p-text-color');
            const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
            const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');

            return {
                maintainAspectRatio: false,
                aspectRatio: 0.6,
                plugins: { 
                    legend: {
                        position: 'bottom', 
                        labels: {
                            boxWidth: 12,
                            padding: 20,
                            usePointStyle: true, 
                            color: textColor,
                            font: {
                                size: 13 
                            }
                        }, 
                    }
                },
                scales: {
                    x: {
                        beginAtZero: false,
                        ticks: {
                            color: textColorSecondary
                        },
                        grid: {
                            color: surfaceBorder
                        }
                    },
                    y: {
                        beginAtZero: false,
                        ticks: {
                            color: textColorSecondary
                        },
                        grace: '5%',
                        grid: {
                            color: surfaceBorder
                        },
                        afterFit: function(scale) {
                            // Гарантируем, что шкала включает все значения
                            if (scale.min > 0) scale.min = 0;
                            if (scale.max < 0) scale.max = 0;
                        }
                    }
                }
            };
        },
    }
};
</script>

<style lang="scss" scoped>

::v-deep .p-card .p-card-body {
    height: 100%!important;
}

::v-deep .p-card .p-card-content {
    height: 100%!important;
}

.adaptiv-container {
    flex-direction: row;
    justify-content: space-between;
}

.adaptiv-container-selectors {
    flex-direction: row;
    justify-content: end;
}

@media (max-width: 768px) {
    .adaptiv-container {
        flex-direction: column;
    }

    .adaptiv-container-selectors {
        flex-direction: column;
    }
}

.adaptiv-height {
    height: 50%;
}

@media (max-width: 768px) {
    .adaptiv-height {
        height: auto;
    }
}

.pai-chart {
    width: 25%;
    height: auto;
}

@media (max-width: 768px) {
    .pai-chart {
        width: auto;
        height: max-content;
    }
}



@media (max-width: 768px) {
    .line-chart {
        width: auto;
        height: max-content;
    }
}

.relative {
  position: relative;
}
.absolute {
  position: absolute;
  z-index: 1;
}
.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.adaptiv-width {
    width: 50%;
}

@media (max-width: 768px) {
    .adaptiv-width {
        width: 100%; 
    }
}

.adaptiv-owerlay {
    flex-wrap: none;
}

@media (max-width: 768px) {
    .adaptiv-owerlay {
        flex-wrap: wrap;
    }
}

.inactive-card {
    flex-direction: row;
}

@media (max-width: 768px) {
    .inactive-card {
        flex-direction: column;
    }
}

</style>